import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { useState } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import QualificationTableData from "Views/Curriculum/data/qualificationTableData";
import ArgonButton from "components/ArgonButton";
import axios from "utils/api";
import { baseURL } from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonInput from "components/ArgonInput";

function Qualification() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Add/Edit form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);
  const [qualificationName, setQualificationName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  // Open Add form
  const handleAddClick = () => {
    setFormMode("add");
    setQualificationName("");
    setEditId(null);
    setFormOpen(true);
  };

  // Open Edit form from table
  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setQualificationName(name || "");
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setQualificationName("");
    setEditId(null);
  };

  // Submit Add/Edit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!qualificationName.trim()) {
      toast.error("Qualification name cannot be empty");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/qualifications`, { name: qualificationName })
        .then((res) => {
          toast.success(res.data?.msg || "Qualification added successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.msg || "Failed to add qualification");
        })
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/qualifications/${editId}`, { name: qualificationName })
        .then((res) => {
          toast.success(res.data?.msg || "Qualification updated successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.msg || "Failed to update qualification");
        })
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    axios
      .delete(`${baseURL}/qualifications/${deleteId}`)
      .then((res) => {
        toast.success(res.data?.msg || "Qualification deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.msg || "Failed to delete qualification");
      });
  };

  const { columns, rows, count } = QualificationTableData(
    handleDeleteClick,
    handleEditClick,
    refresh
  );

  return (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={4} mb={3}>
        <ArgonTypography variant="h6" size="small">
          All Qualification
        </ArgonTypography>
        <ArgonButton variant="contained" color="primary" size="small" onClick={handleAddClick}>
          Add Qualification
        </ArgonButton>
      </ArgonBox>
      <ArgonBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              No qualification found
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Click &apos;Add Qualification&apos; to create a new qualification.
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Qualification"
        description="Are you sure you want to delete this qualification?"
        confirmText="Delete Qualification"
        cancelText="Cancel"
      />

      {/* Add/Edit Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5" fontWeight="medium">
              {formMode === "add" ? "Add New Qualification" : "Edit Qualification"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                Qualification Name *
              </ArgonTypography>
              <ArgonInput
                type="text"
                placeholder="Enter qualification name"
                value={qualificationName}
                onChange={(e) => setQualificationName(e.target.value)}
                fullWidth
                required
              />
            </ArgonBox>
          </DialogContent>
          <Divider />
          <DialogActions>
            <ArgonButton onClick={handleCloseForm} color="secondary" variant="outlined">
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" color="primary" variant="contained" disabled={formLoading}>
              {formLoading ? "Saving..." : formMode === "add" ? "Add" : "Update"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
    </ArgonBox>
  );
}

export default Qualification;
